/**
 * DocSidebarAttachmentsSection Component
 * 
 * Displays the attachments section of the document sidebar.
 * Handles attachment listing, uploading, and deletion.
 */

import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, X } from "lucide-react";
import { useAttachments, useDeleteAttachment } from "@/lib/attachments/hooks";
import { AttachmentPickerDialog } from "@/components/attachments";
import { formatFileSize } from "@/lib/attachments/utils";
import type { AttachmentContext, Attachment, GetAttachmentsResponse } from "@/lib/attachments/types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import type { DocSidebarData } from "@/lib/erp/docs";

type DocSidebarAttachmentsSectionProps = {
  doctype: string;
  name: string;
  editable?: boolean;
  sidebarAttachments?: DocSidebarData["attachments"];
};

export function DocSidebarAttachmentsSection({
  doctype,
  name,
  editable = false,
  sidebarAttachments,
}: DocSidebarAttachmentsSectionProps) {
  const { language } = useLanguage();
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      attachments: getUI('attachments', 'Attachments'),
      deleteAttachmentConfirm: getUI('delete_attachment_confirm', 'Delete this attachment?'),
    };
  }, [language]);

  // Attachment context (doc-level, no fieldname)
  const attachmentContext: AttachmentContext = { doctype, name };

  // Generic attachment hooks
  const { data: attachmentsData, isLoading: isLoadingAttachments } = useAttachments(
    attachmentContext,
    { enabled: !!name }
  );
  const deleteAttachmentMutation = useDeleteAttachment(attachmentContext);

  // Merge attachment data: prefer hook data (real-time), fallback to sidebar data
  const attachments = useMemo<Attachment[]>(() => {
    // If the hook has run, always trust it – even if it's empty
    // This ensures deleted attachments disappear immediately
    if (attachmentsData) {
      return attachmentsData.attachments ?? [];
    }

    // Fallback only before the hook has loaded once (initial SSR / first paint)
    const sidebarAtts = sidebarAttachments ?? [];
    if (sidebarAtts.length > 0) {
      // Transform to Attachment shape
      return sidebarAtts.map(att => ({
        docname: att.name,
        file_name: att.file_name,
        file_url: att.file_url,
        mime_type: undefined as string | undefined,
        file_size: undefined as number | undefined,
        is_private: 0,
        attached_to_field: undefined as string | undefined,
        creation: att.creation,
      })) as Attachment[];
    }

    return [];
  }, [attachmentsData, sidebarAttachments]);

  // Handle attachment dialog completion
  const handleAttachCompleted = (_result?: GetAttachmentsResponse) => {
    // Dialog already handled the attach via hooks
    // Cache will update automatically
    // Do NOT close the dialog here - let user close it manually

    // Also invalidate sidebar query to ensure counts/metadata stay in sync
    queryClient.invalidateQueries({
      queryKey: ["doc-sidebar", doctype, name],
    });
  };

  // Handle delete attachment
  const handleDeleteFile = async (attachment: Attachment) => {
    if (!confirm(uiLabels.deleteAttachmentConfirm)) return;

    try {
      // Prefer docname over file_url (safer)
      await deleteAttachmentMutation.mutateAsync(
        attachment.docname
          ? { file_docname: attachment.docname }
          : { file_url: attachment.file_url }
      );

      // Also invalidate sidebar query to ensure counts/metadata stay in sync
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    } catch (error) {
      // Error is handled by mutation hook
      console.error("Failed to delete attachment:", error);
    }
  };

  if (attachments.length === 0 && !editable) {
    return null;
  }

  return (
    <>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground/80 uppercase tracking-[0.5px]">
            <Paperclip className="h-3.5 w-3.5 text-muted-foreground/70" />
            <span>{uiLabels.attachments}</span>
          </div>
          {editable && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 focus:outline-none focus:ring-0 touch-manipulation sm:h-9 sm:w-9"
              onClick={() => setIsAttachmentDialogOpen(true)}
              disabled={deleteAttachmentMutation.isPending}
            >
              <Upload className="w-5 h-5 transition-transform duration-150 sm:h-4 sm:w-4" />
              <span className="sr-only">Attach files</span>
            </Button>
          )}
        </div>
        {/* Loading state */}
        {isLoadingAttachments && attachments.length === 0 && (
          <div className="text-sm text-muted-foreground py-2">
            Loading attachments...
          </div>
        )}

        {/* Empty state */}
        {!isLoadingAttachments && attachments.length === 0 && (
          <div className="text-sm text-muted-foreground py-2">
            No attachments yet
          </div>
        )}

        {/* Attachments list */}
        {attachments.length > 0 && (
          <div className="space-y-2">
            {attachments.map((attachment: Attachment) => (
              <div
                key={attachment.docname || attachment.file_url}
                className="flex items-center gap-2.5 sm:gap-2 group rounded bg-muted/30 hover:bg-muted/50 border border-border/30 px-3.5 py-2.5 sm:px-3 sm:py-2 transition-all duration-200 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.1)]"
              >
                <a
                  href={attachment.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2.5 sm:gap-2 text-sm sm:text-xs text-foreground hover:text-foreground truncate transition-colors flex-1 min-w-0 font-medium"
                >
                  <Paperclip className="h-4 w-4 sm:h-3.5 sm:w-3.5 flex-shrink-0 text-muted-foreground/70" />
                  <div className="flex-1 min-w-0">
                    <span className="truncate block">{attachment.file_name}</span>
                    {attachment.file_size && (
                      <span className="text-xs text-muted-foreground/70">
                        {formatFileSize(attachment.file_size)}
                      </span>
                    )}
                  </div>
                </a>
                {editable && (
                  <button
                    type="button"
                    onClick={() => handleDeleteFile(attachment)}
                    disabled={deleteAttachmentMutation.isPending}
                    className="p-2 sm:p-1.5 bg-muted/60 hover:bg-muted/80 text-muted-foreground/70 hover:text-foreground rounded-full touch-manipulation min-h-[36px] min-w-[36px] sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center active:scale-90 transition-all duration-150"
                    aria-label={`Delete ${attachment.file_name}`}
                  >
                    <X className="h-4 w-4 sm:h-3.5 sm:w-3.5 transition-colors" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      {(attachments.length > 0 || editable) && (
        <div className="border-t border-border/30" />
      )}

      {/* Attachment Picker Dialog */}
      <AttachmentPickerDialog
        open={isAttachmentDialogOpen}
        onOpenChange={setIsAttachmentDialogOpen}
        context={attachmentContext}
        multiple={true}
        onCompleted={handleAttachCompleted}
      />
    </>
  );
}
